import { spLib } from './lib';

export type SearchProps = {
  townFrom?: unknown;
  countryTo?: unknown;
  townsTo?: unknown;
  nights?: unknown;
  nightsTo?: unknown;
  depFromDate?: unknown;
  depToDate?: unknown;
  adt?: unknown;
  childrenAge?: unknown;
  hotels?: unknown;
  stars?: unknown;
  meal?: unknown;
  isKgf?: unknown;
  minPrice?: unknown;
  maxPrice?: unknown;
  sortBy?: unknown;
  sortOrder?: unknown;
};

export enum SearchFrom {
  POPULAR_TOURS = 'popular-tours',
  HOT_TOURS = 'hot-tours',
}

/**
 * @description Class to work with search params
 *
 * Search params are build based on unsafe object, so we need to
 * validate all the values to be sure that they are valid
 *
 * SearchParams are build from a url search query object or a plain object
 */
export class SearchParams {
  townFromId: number;

  countryToId: number;

  townsToId?: number[];

  passengers: {
    adt: number;
    childrenAge?: number[];
  };

  /**
   * @description Start of the interval of possible departure dates.
   * */
  depFromDate: string;

  /**
   * @description End of the interval of possible departure dates.
   *
   * If the search is not flexible, depToDate should be the same as depFromDate.
   */
  depToDate: string;

  /**
   * @description Start of the interval of possible amount of nights.
   * */
  numberOfNights: number;

  /**
   * @description End of the interval of possible amount of nights.
   *
   * If the search is not flexible, numberOfNightsTo should be the same as numberOfNights.
   */
  numberOfNightsTo: number;

  /**
   * @description Search filter params
   */
  additionalParams: {
    hotels?: string[];
    stars?: string[];
    meal?: string[];
    isKgf?: boolean;
    minPrice?: number;
    maxPrice?: number;
    sortBy?: string;
    sortOrder?: string;
  };

  constructor(unsafeParams: SearchProps) {
    this.townFromId = spLib.reqParseToNum(unsafeParams.townFrom);
    this.countryToId = spLib.reqParseToNum(unsafeParams.countryTo);
    this.townsToId = spLib.parseToNumArr(unsafeParams.townsTo);

    /** @note It was decided to leave `nightsTo` as a required parameter,
     * because it is used by Hot tours page where it is required for secondary search
     *
     * If you're getting an error saying that `nightsTo` is required,  but you
     * don't have a value for it, simply duplicate the value of `nights` parameter
     *
     * TODO:  Make `nightsTo` not required by default
     */
    this.numberOfNights = spLib.reqParseToNum(unsafeParams.nights);
    this.numberOfNightsTo = spLib.reqParseToNum(unsafeParams.nightsTo);
    this.depFromDate = spLib.reqParseToStr(unsafeParams.depFromDate);
    this.depToDate = spLib.reqParseToStr(unsafeParams.depToDate);
    this.passengers = {
      adt: spLib.reqParseToNum(unsafeParams.adt),
      childrenAge: spLib.parseToNumArr(unsafeParams.childrenAge),
    };
    this.additionalParams = spLib.parseAdditionalParams({
      hotels: unsafeParams.hotels,
      stars: unsafeParams.stars,
      meal: unsafeParams.meal,
      isKgf: unsafeParams.isKgf,
      minPrice: unsafeParams.minPrice,
      maxPrice: unsafeParams.maxPrice,
      sortBy: unsafeParams.sortBy,
      sortOrder: unsafeParams.sortOrder,
    });
  }

  /**
   * @description Method to convert values to supported queries values
   *
   * Since all parameters are to be converted to VueRouter search query,
   * there should not be nesting and all values should be strings
   *
   * @see https://router.vuejs.org/api/#LocationQueryRaw
   */
  buildQueryParams() {
    const { adt, childrenAge } = this.passengers;

    const rawAdditionalParams = {
      hotels: this.additionalParams.hotels?.join(','),
      stars: this.additionalParams.stars?.join(','),
      meal: this.additionalParams.meal?.join(','),
      isKgf: this.additionalParams.isKgf ? '1' : '',
      minPrice: this.additionalParams.minPrice?.toString(),
      maxPrice: this.additionalParams.maxPrice?.toString(),
      sortBy: this.additionalParams.sortBy?.toString(),
      sortOrder: this.additionalParams.sortOrder?.toString(),
    };

    const rawParams = {
      townFrom: this.townFromId.toString(),
      countryTo: this.countryToId.toString(),
      townsTo: this.townsToId?.join(','),
      nights: this.numberOfNights.toString(),
      nightsTo: this.numberOfNightsTo.toString(),
      depFromDate: this.depFromDate,
      depToDate: this.depToDate,
      adt: adt.toString(),
      childrenAge: childrenAge?.join(','),
      ...rawAdditionalParams,
    };

    return spLib.skipEmptyParams(rawParams);
  }

  toQueryString() {
    const queryParams = this.buildQueryParams();
    return new URLSearchParams(queryParams).toString();
  }
}
